import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { getPlannerAnalytics } from "./plannerApi";

const SECTION_LABELS = {
    cars: "Car purchase", house: "House purchase", dependants: "Dependants' upkeep",
    children: "Children (education/marriage)", inheritance: "Inheritance", emis: "EMIs",
    misc: "Miscellaneous", reduced_growth: "Reduced growth rate",
};

const Stat = ({ label, value, sub }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white px-5 py-4">
        <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{label}</div>
        <div className="font-display text-2xl font-semibold tracking-tight mt-1">{value}</div>
        {sub && <div className="text-xs text-oth-ink3 font-body mt-0.5">{sub}</div>}
    </div>
);

const pct = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "—");

export const PlannerAnalyticsAdmin = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        getPlannerAnalytics(days).then(setData).catch(() => toast.error("Could not load analytics."));
    }, [days]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="font-editorial italic text-oth-ink3">Loading…</div>;
    const f = data.funnel;
    const funnelSteps = [
        { label: "Visited /diy", value: f.visits, prev: null },
        { label: "Entered investments", value: f.started, prev: f.visits },
        { label: "Viewed projection", value: f.projection, prev: f.started },
        { label: "Saved a plan", value: f.saved, prev: f.projection },
        { label: "Submitted to OTH", value: f.submitted, prev: f.saved },
    ];
    const maxSection = Math.max(1, ...data.sections.map((s) => s.n));

    return (
        <div className="space-y-6" data-testid="planner-analytics-admin">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-oth-ink3 font-body max-w-xl">Anonymous usage only — no amounts, answers or plan contents are ever tracked. Sessions reset when a visitor closes their tab.</p>
                <div className="flex gap-1.5">
                    {[7, 30, 90].map((d) => (
                        <button key={d} onClick={() => setDays(d)} className={`px-3.5 py-1.5 rounded-full border text-xs font-body transition-colors ${days === d ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-oth-line text-oth-ink2"}`} data-testid={`analytics-range-${d}`}>
                            {d} days
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Unique visitors" value={f.visits} sub={`${data.devices.mobile || 0} mobile · ${data.devices.desktop || 0} desktop`} />
                <Stat label="Plans saved" value={f.saved} sub={`${pct(f.saved, f.visits)} of visitors`} />
                <Stat label="Submitted to OTH" value={f.submitted} sub={`${pct(f.submitted, f.saved)} of saved plans`} />
                <Stat label="Downloads" value={data.counts.excel_downloads + data.counts.pdf_downloads} sub={`${data.counts.excel_downloads} Excel · ${data.counts.pdf_downloads} PDF · ${data.counts.unlocks} return unlocks`} />
            </div>

            <div className="rounded-2xl border border-oth-line/70 bg-white p-6" data-testid="analytics-funnel">
                <h3 className="font-display text-base font-semibold tracking-tight mb-4">Conversion funnel</h3>
                <div className="space-y-3">
                    {funnelSteps.map((s) => (
                        <div key={s.label} className="flex items-center gap-4">
                            <div className="w-44 shrink-0 text-sm font-body text-oth-ink2">{s.label}</div>
                            <div className="flex-1 h-7 bg-[#f0f0ec] rounded-lg overflow-hidden">
                                <div className="h-full bg-[#0B3B60] rounded-lg transition-all" style={{ width: `${f.visits > 0 ? Math.max(2, (s.value / Math.max(1, f.visits)) * 100) : 0}%` }} />
                            </div>
                            <div className="w-24 shrink-0 text-right text-sm font-body tabular-nums">
                                <span className="font-semibold">{s.value}</span>
                                {s.prev !== null && <span className="text-oth-ink3 ml-1.5 text-xs">{pct(s.value, s.prev)}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-2xl border border-oth-line/70 bg-white p-6" data-testid="analytics-trend">
                <h3 className="font-display text-base font-semibold tracking-tight mb-4">Daily activity</h3>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data.daily} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e3dd" />
                            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b67" }} tickLine={false} axisLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b6b67" }} width={32} tickLine={false} axisLine={false} />
                            <ChartTooltip contentStyle={{ borderRadius: 12, border: "1px solid #d9d7d1", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Area type="monotone" dataKey="visits" name="Visitors" stroke="#0B3B60" strokeWidth={2} fill="#0B3B6022" />
                            <Area type="monotone" dataKey="saves" name="Plans saved" stroke="#0a7d4f" strokeWidth={2} fill="#0a7d4f22" />
                            <Area type="monotone" dataKey="submissions" name="Submitted" stroke="#b45309" strokeWidth={2} fill="#b4530922" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="rounded-2xl border border-oth-line/70 bg-white p-6" data-testid="analytics-sections">
                <h3 className="font-display text-base font-semibold tracking-tight mb-1">Cash-flow sections used</h3>
                <p className="text-xs text-oth-ink3 font-body mb-4">Unique sessions that switched each section on — shows which goals matter most to your visitors.</p>
                {data.sections.length === 0 ? (
                    <div className="text-sm font-body text-oth-ink3">No section usage recorded yet.</div>
                ) : (
                    <div className="space-y-2.5">
                        {data.sections.map((s) => (
                            <div key={s.section} className="flex items-center gap-4">
                                <div className="w-56 shrink-0 text-sm font-body text-oth-ink2">{SECTION_LABELS[s.section] || s.section}</div>
                                <div className="flex-1 h-5 bg-[#f0f0ec] rounded overflow-hidden">
                                    <div className="h-full bg-[#0B3B60]/70 rounded" style={{ width: `${(s.n / maxSection) * 100}%` }} />
                                </div>
                                <div className="w-10 shrink-0 text-right text-sm font-body tabular-nums font-semibold">{s.n}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
